/**
 * Multi-Step Bundle Builder
 * Handles step accordion, variant selection, and cart functionality
 */

class MultiStepBundle {
  constructor() {
    this.container = document.querySelector(".multi-step-bundle-wrapper");
    if (!this.container) return;

    this.steps = this.container.querySelectorAll(".bundle-step");
    this.mainImage = document.getElementById("bundle-main-image");
    this.addToCartBtn = document.getElementById("bundle-add-to-cart-btn");
    this.selectedVariants = new Map();
    this.sectionId = this.container.dataset.sectionId;

    this.init();
  }

  init() {
    console.log("[Multi-Step Bundle] Initializing...");

    // Set up accordion functionality
    this.initAccordions();

    // Set up variant selection
    this.initVariantSelectors();

    // Set up confirm buttons
    this.initConfirmButtons();

    // Set up add to cart
    this.initAddToCart();

    // Open first step by default
    this.openDefaultStep();

    console.log("[Multi-Step Bundle] Initialized successfully");
  }

  /**
   * Initialize accordion toggle functionality
   */
  initAccordions() {
    this.steps.forEach((step) => {
      const header = step.querySelector(".bundle-step-header");
      const content = step.querySelector(".bundle-step-content");

      if (!header || !content) return;

      header.addEventListener("click", () => {
        this.toggleStep(step, content, header);
      });
    });
  }

  /**
   * Toggle a step open/closed
   */
  toggleStep(step, content, header) {
    const isActive = step.classList.contains("active");
    const stepNumber = step.dataset.step;
    const isConfirmed = step.classList.contains("confirmed");

    // Close all other steps first
    this.steps.forEach((s) => {
      if (s !== step) {
        s.classList.remove("active");
        const otherContent = s.querySelector(".bundle-step-content");
        const otherHeader = s.querySelector(".bundle-step-header");
        if (otherContent) otherContent.style.display = "none";
        if (otherHeader) otherHeader.setAttribute("aria-expanded", "false");
      }
    });

    // Toggle current step
    if (isActive) {
      step.classList.remove("active");
      step.classList.remove("editing"); // Remove editing state when closing
      content.style.display = "none";
      header.setAttribute("aria-expanded", "false");
    } else {
      step.classList.add("active");
      content.style.display = "block";
      header.setAttribute("aria-expanded", "true");

      // If reopening a confirmed step, allow editing
      if (isConfirmed) {
        step.classList.add("editing"); // Add editing state
        const confirmBtn = step.querySelector(".step-confirm-btn");
        if (confirmBtn) {
          confirmBtn.disabled = false;
          confirmBtn.textContent = "UPDATE SELECTION";
        }
      }

      // Update main image to this step's current image
      this.updateMainImage(step);
    }
  }

  /**
   * Initialize variant selection dropdowns (custom-select)
   */
  initVariantSelectors() {
    const customSelects = this.container.querySelectorAll(
      ".step-variant-option custom-select"
    );

    customSelects.forEach((customSelect) => {
      customSelect.addEventListener("change", (e) => {
        this.handleVariantChange(e);
      });

      // Trigger initial change to set default variant
      customSelect.dispatchEvent(new Event("change"));
    });
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(event) {
    const customSelect = event.target.closest("custom-select");
    if (!customSelect) return;

    const step = customSelect.closest(".bundle-step");
    const stepNumber = step.dataset.step;
    const allCustomSelects = step.querySelectorAll(
      ".step-variant-option custom-select"
    );

    // Get all selected options for this step
    const selectedOptions = Array.from(allCustomSelects).map((cs) => {
      const selected = cs.querySelector(
        '.custom-select__option[aria-selected="true"]'
      );
      return selected ? selected.dataset.value : null;
    });

    // Get variant data from JSON
    const variantsDataEl = step.querySelector(".step-variants-data");
    if (!variantsDataEl) {
      console.warn(
        "[Multi-Step Bundle] No variant data found for step",
        stepNumber
      );
      return;
    }

    let variantsData;
    try {
      variantsData = JSON.parse(variantsDataEl.textContent);
    } catch (e) {
      console.error("[Multi-Step Bundle] Error parsing variant data:", e);
      return;
    }

    // Find matching variant
    const matchedVariant = variantsData.find((variant) =>
      variant.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (matchedVariant) {
      console.log("[Multi-Step Bundle] Matched variant:", matchedVariant);

      // Update step image if variant has an image
      const stepImg = step.querySelector("[data-step-image]");
      if (matchedVariant.featured_image && stepImg) {
        stepImg.src = matchedVariant.featured_image.src;
      }

      // Update main image if this step is currently active
      if (step.classList.contains("active")) {
        this.updateMainImage(step);
      }

      // Store the selected variant (but not confirmed yet)
      const productData = step.querySelector(".step-product-data");
      step.tempSelectedVariant = {
        variantId: matchedVariant.id,
        price: matchedVariant.price,
        title: matchedVariant.title,
        options: selectedOptions,
        productTitle: productData ? productData.dataset.productTitle : "",
        available: matchedVariant.available,
      };

      // Enable/disable confirm button based on availability
      const confirmBtn = step.querySelector(".step-confirm-btn");
      if (confirmBtn && !step.classList.contains("confirmed")) {
        confirmBtn.disabled = !matchedVariant.available;
        confirmBtn.textContent = matchedVariant.available
          ? "CONFIRM"
          : "OUT OF STOCK";
      }
    } else {
      console.warn(
        "[Multi-Step Bundle] No matching variant found for options:",
        selectedOptions
      );
    }
  }

  /**
   * Initialize confirm buttons
   */
  initConfirmButtons() {
    const confirmBtns = this.container.querySelectorAll(".step-confirm-btn");

    confirmBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        this.confirmStep(e);
      });
    });
  }

  /**
   * Confirm a step's selection
   */
  confirmStep(event) {
    const btn = event.target;
    const step = btn.closest(".bundle-step");
    const stepNumber = step.dataset.step;
    const wasAlreadyConfirmed = step.classList.contains("confirmed");

    // Check if variant is selected
    if (!step.tempSelectedVariant) {
      alert("Please select all options before confirming.");
      return;
    }

    // Store confirmed selection (updates if already confirmed)
    this.selectedVariants.set(stepNumber, step.tempSelectedVariant);

    // Mark step as confirmed and remove editing state
    step.classList.add("confirmed");
    step.classList.remove("editing");
    btn.textContent = "✓ CONFIRMED";
    btn.disabled = true;

    console.log(
      "[Multi-Step Bundle] Step " +
        (wasAlreadyConfirmed ? "updated" : "confirmed") +
        ":",
      stepNumber,
      step.tempSelectedVariant
    );

    // Update add to cart button state
    this.updateAddToCartButton();

    // Auto-advance to next step if enabled (only for first-time confirmation)
    if (!wasAlreadyConfirmed) {
      const autoAdvance = this.container.querySelector("[data-auto-advance]");
      if (!autoAdvance || autoAdvance.dataset.autoAdvance !== "false") {
        this.advanceToNextStep(step);
      }
    }
  }

  /**
   * Advance to the next step
   */
  advanceToNextStep(currentStep) {
    const nextStep = currentStep.nextElementSibling;

    if (nextStep && nextStep.classList.contains("bundle-step")) {
      const nextHeader = nextStep.querySelector(".bundle-step-header");
      if (nextHeader) {
        // Small delay for better UX
        setTimeout(() => {
          nextHeader.click();
        }, 300);
      }
    }
  }

  /**
   * Update main product image
   */
  updateMainImage(step) {
    // Check if a custom hero image is set
    const hasCustomHero =
      this.mainImage && this.mainImage.dataset.customHero === "true";

    // If custom hero is set, never overwrite it
    if (hasCustomHero) {
      return;
    }

    const stepImage = step.querySelector("[data-step-image]");

    if (stepImage && this.mainImage) {
      this.mainImage.src = stepImage.src;
      this.mainImage.alt = stepImage.alt;
    }
  }

  /**
   * Open the default step (first one or one marked as open_by_default)
   */
  openDefaultStep() {
    let defaultStep = null;

    // First, try to find a step with open_by_default="true"
    this.steps.forEach((step) => {
      const header = step.querySelector(".bundle-step-header");
      if (header && header.dataset.open === "true") {
        defaultStep = step;
      }
    });

    // If no default found, use first step
    if (!defaultStep && this.steps.length > 0) {
      defaultStep = this.steps[0];
    }

    // Open the default step
    if (defaultStep) {
      const header = defaultStep.querySelector(".bundle-step-header");
      if (header && !defaultStep.classList.contains("active")) {
        header.click();
      }
    }
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartButton() {
    if (!this.addToCartBtn) return;

    // Check if all required steps are confirmed
    const requiredSteps = Array.from(this.steps).filter(
      (step) => step.dataset.required === "true"
    );

    const allRequiredConfirmed = requiredSteps.every((step) =>
      step.classList.contains("confirmed")
    );

    // Enable/disable button
    this.addToCartBtn.disabled = !allRequiredConfirmed;

    // Update button text to show progress
    const confirmedCount = Array.from(this.steps).filter((step) =>
      step.classList.contains("confirmed")
    ).length;
    const totalSteps = this.steps.length;

    if (allRequiredConfirmed) {
      this.addToCartBtn.textContent = "ADD TO CART";
    } else {
      this.addToCartBtn.textContent = `SELECT ALL OPTIONS (${confirmedCount}/${totalSteps})`;
    }
  }

  /**
   * Initialize add to cart functionality
   */
  initAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener("click", () => {
      this.addBundleToCart();
    });
  }

  /**
   * Add all selected variants to cart (individual products)
   */
  async addBundleToCart() {
    // Validate all required steps are confirmed
    const requiredSteps = Array.from(this.steps).filter(
      (step) => step.dataset.required === "true"
    );

    const allRequiredConfirmed = requiredSteps.every((step) =>
      step.classList.contains("confirmed")
    );

    if (!allRequiredConfirmed) {
      alert("Please complete all required steps before adding to cart.");
      return;
    }

    // Disable button and show loading state
    this.addToCartBtn.disabled = true;
    const originalText = this.addToCartBtn.textContent;
    this.addToCartBtn.textContent = "ADDING...";

    // Prepare items for cart (individual products)
    const items = Array.from(this.selectedVariants.values()).map((variant) => ({
      id: variant.variantId,
      quantity: 1,
      properties: {
        "_Part of Bundle": "Multi-Step Bundle",
      },
    }));

    console.log("[Multi-Step Bundle] Adding items to cart ONE BY ONE:", items);

    // Check if jQuery is available (most Shopify themes have it)
    if (typeof jQuery !== "undefined") {
      console.log("[Multi-Step Bundle] Using jQuery AJAX for cart add");

      // Add products ONE AT A TIME (sequentially) like regular product pages
      // This is what Opus Cart expects - single item adds
      let currentIndex = 0;
      const self = this;

      function addNextItem() {
        if (currentIndex >= items.length) {
          // All items added successfully
          console.log("[Multi-Step Bundle] All items added successfully!");
          return;
        }

        const item = items[currentIndex];
        console.log(
          `[Multi-Step Bundle] Adding item ${currentIndex + 1} of ${
            items.length
          }:`,
          item
        );

        // Add single item with ?opus=true parameter (just like regular products)
        jQuery.ajax({
          type: "POST",
          url: "/cart/add.js?opus=true",
          data: JSON.stringify({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties,
          }),
          contentType: "application/json",
          dataType: "json",
          success: (data) => {
            console.log(
              `[Multi-Step Bundle] Item ${
                currentIndex + 1
              } added successfully:`,
              data
            );
            currentIndex++;

            // Continue adding next item
            if (currentIndex < items.length) {
              addNextItem();
            } else {
              console.log(
                "[Multi-Step Bundle] All items added - checking for Opus Cart drawer..."
              );

              // Wait briefly then trigger the cart drawer
              setTimeout(function () {
                console.log(
                  "[Multi-Step Bundle] Looking for cart drawer button to click..."
                );

                // Find the cart drawer trigger button (same one user clicks manually)
                const cartTriggers = document.querySelectorAll(
                  'a[href="/cart"], button[data-cart-drawer], [data-opus-cart-trigger], .cart-link, .header__cart-toggle, [aria-controls*="cart"]'
                );

                if (cartTriggers.length > 0) {
                  console.log(
                    "[Multi-Step Bundle] Found cart trigger, clicking it..."
                  );
                  // Reset button state
                  self.addToCartBtn.disabled = false;
                  self.addToCartBtn.textContent = originalText;
                  // Click the first cart trigger (usually in the header)
                  cartTriggers[0].click();
                } else {
                  console.log(
                    "[Multi-Step Bundle] No cart trigger found, redirecting to /cart"
                  );
                  window.location.href = "/cart";
                }
              }, 100);
            }
          },
          error: (xhr, status, error) => {
            console.error("[Multi-Step Bundle] Error adding item:", error);
            const errorData = xhr.responseJSON || {};
            alert(
              "Sorry, there was an error adding the bundle to your cart. Please try again.\n\nError: " +
                (errorData.description || errorData.message || error)
            );
            // Re-enable button
            self.addToCartBtn.disabled = false;
            self.addToCartBtn.textContent = originalText;
          },
        });
      }

      // Start adding items one by one
      addNextItem();
    } else {
      // Fallback to XMLHttpRequest if jQuery not available
      console.log("[Multi-Step Bundle] jQuery not found, using XMLHttpRequest");

      const self = this;
      let currentIndex = 0;

      function addNextItemXHR() {
        if (currentIndex >= items.length) {
          console.log("[Multi-Step Bundle] All items added successfully!");
          return;
        }

        const item = items[currentIndex];
        console.log(
          `[Multi-Step Bundle] Adding item ${currentIndex + 1} of ${
            items.length
          }:`,
          item
        );

        const xhr = new XMLHttpRequest();

        // IMPORTANT: Add ?opus=true parameter for Opus Cart to intercept
        xhr.open("POST", "/cart/add.js?opus=true", true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");

        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            console.log(
              `[Multi-Step Bundle] Item ${
                currentIndex + 1
              } added successfully:`,
              data
            );
            currentIndex++;

            // Continue adding next item
            if (currentIndex < items.length) {
              addNextItemXHR();
            } else {
              console.log(
                "[Multi-Step Bundle] All items added - checking for Opus Cart drawer..."
              );

              // Wait briefly then trigger the cart drawer
              setTimeout(function () {
                console.log(
                  "[Multi-Step Bundle] Looking for cart drawer button to click..."
                );

                // Find the cart drawer trigger button (same one user clicks manually)
                const cartTriggers = document.querySelectorAll(
                  'a[href="/cart"], button[data-cart-drawer], [data-opus-cart-trigger], .cart-link, .header__cart-toggle, [aria-controls*="cart"]'
                );

                if (cartTriggers.length > 0) {
                  console.log(
                    "[Multi-Step Bundle] Found cart trigger, clicking it..."
                  );
                  // Reset button state
                  self.addToCartBtn.disabled = false;
                  self.addToCartBtn.textContent = originalText;
                  // Click the first cart trigger (usually in the header)
                  cartTriggers[0].click();
                } else {
                  console.log(
                    "[Multi-Step Bundle] No cart trigger found, redirecting to /cart"
                  );
                  window.location.href = "/cart";
                }
              }, 500);
            }
          } else {
            const errorData = JSON.parse(xhr.responseText || "{}");
            alert(
              "Sorry, there was an error adding the bundle to your cart. Please try again.\n\nError: " +
                (errorData.description || errorData.message || "Unknown error")
            );
            self.addToCartBtn.disabled = false;
            self.addToCartBtn.textContent = originalText;
          }
        };

        xhr.onerror = function () {
          alert("Sorry, there was a network error. Please try again.");
          self.addToCartBtn.disabled = false;
          self.addToCartBtn.textContent = originalText;
        };

        // Send single item
        xhr.send(
          JSON.stringify({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties,
          })
        );
      }

      // Start adding items one by one with XMLHttpRequest
      addNextItemXHR();
    }
  }
}

// Initialize when DOM is ready and custom-select is defined
function initBundle() {
  if (customElements.get("custom-select")) {
    new MultiStepBundle();
  } else {
    customElements.whenDefined("custom-select").then(() => {
      new MultiStepBundle();
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBundle);
} else {
  initBundle();
}

// Re-initialize on Shopify section reload (Theme Editor)
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector(".multi-step-bundle-wrapper")) {
    initBundle();
  }
});
